/**
 * WizardCard component for editing Wizard or Apprentice.
 * Enforces item caps (Wizard: 5, Apprentice: 4) from Wizard Sheet.
 * Shows combat stats - Apprentice stats are Wizard stats -2 (except Move).
 */

import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Badge,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui';
import {
  Plus,
  Trash2,
  Wand2,
  User,
  Heart,
  Star,
  Info,
  Shield,
  Swords,
  Target,
  Footprints,
  Brain,
} from 'lucide-react';
import { cn } from '@/utils';
import type { Caster, CasterStats, Item, School } from '@/store/warbandSlice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WizardCardProps {
  caster: Caster;
  role: 'wizard' | 'apprentice';
  onUpdate: (caster: Caster) => void;
  onRemove?: () => void; // Only for apprentice
  wizardStats?: CasterStats; // For apprentice to show derived stats info
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SCHOOLS: School[] = [
  'Chronomancer',
  'Elementalist',
  'Enchanter',
  'Illusionist',
  'Necromancer',
  'Sigilist',
  'Soothsayer',
  'Summoner',
  'Thaumaturge',
  'Witch',
];

// ---------------------------------------------------------------------------
// Stat Input Component
// ---------------------------------------------------------------------------

interface StatInputProps {
  icon: React.ReactNode;
  label: string;
  id: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  hint?: string | undefined;
}

function StatInput({ icon, label, id, value, onChange, disabled, hint }: StatInputProps) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="flex items-center gap-1 text-xs">
        {icon}
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        disabled={disabled}
        className={cn('h-9 text-center font-mono', disabled && 'bg-muted')}
      />
      {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Default stats fallback for old data without stats
const DEFAULT_STATS: CasterStats = {
  move: 6,
  fight: 2,
  shoot: 0,
  armor: 10,
  will: 4,
};

export function WizardCard({ caster, role, onUpdate, onRemove, wizardStats }: WizardCardProps) {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');

  const isWizard = role === 'wizard';
  const itemCap = caster.maxItems;
  const canAddItem = caster.items.length < itemCap;

  // Ensure stats exist (fallback for old data)
  const stats = caster.stats ?? DEFAULT_STATS;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleNameChange = (name: string) => {
    onUpdate({ ...caster, name });
  };

  const handleSchoolChange = (school: School) => {
    onUpdate({ ...caster, school });
  };

  const handleLevelChange = (level: number) => {
    onUpdate({ ...caster, level: Math.max(0, level) });
  };

  const handleStatChange = (stat: keyof CasterStats, value: number) => {
    onUpdate({
      ...caster,
      stats: { ...stats, [stat]: value },
    });
  };

  const handleHealthChange = (current: number) => {
    onUpdate({
      ...caster,
      health: { ...caster.health, current: Math.max(0, current) },
    });
  };

  const handleMaxHealthChange = (max: number) => {
    const newMax = Math.max(1, max);
    onUpdate({
      ...caster,
      health: { max: newMax, current: Math.min(caster.health.current, newMax) },
    });
  };

  const handleXpChange = (xp: number) => {
    onUpdate({ ...caster, xp: Math.max(0, xp) });
  };

  const handleNotesChange = (notes: string) => {
    onUpdate({ ...caster, notes });
  };

  const handleAddItem = () => {
    if (!newItemName.trim() || !canAddItem) return;

    const newItem: Item = {
      id: `item-${Date.now()}`,
      name: newItemName.trim(),
    };

    onUpdate({
      ...caster,
      items: [...caster.items, newItem],
    });

    setNewItemName('');
    setIsAddingItem(false);
  };

  const handleRemoveItem = (itemId: string) => {
    onUpdate({
      ...caster,
      items: caster.items.filter((item) => item.id !== itemId),
    });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Card className={cn('w-full', isWizard && 'border-primary/30')}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isWizard ? (
              <Wand2 className="h-5 w-5 text-primary" />
            ) : (
              <User className="h-5 w-5 text-muted-foreground" />
            )}
            <CardTitle>{isWizard ? 'Wizard' : 'Apprentice'}</CardTitle>
          </div>
          {!isWizard && onRemove && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRemove}
              aria-label="Remove apprentice"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <CardDescription>
          {isWizard
            ? 'Your wizard leads the warband'
            : 'Stats are Wizard -2 (except Move). Casts at -2.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Name & School */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${role}-name`}>Name</Label>
            <Input
              id={`${role}-name`}
              value={caster.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={isWizard ? 'Enter wizard name' : 'Enter apprentice name'}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${role}-school`}>School</Label>
            <select
              id={`${role}-school`}
              value={caster.school}
              onChange={(e) => handleSchoolChange(e.target.value as School)}
              disabled={!isWizard} // Apprentice inherits wizard's school
              className={cn(
                'flex h-11 w-full rounded-md border border-input bg-background px-3 py-2',
                'text-sm focus-visible:outline-none focus-visible:ring-2',
                'focus-visible:ring-ring focus-visible:ring-offset-2',
                !isWizard && 'bg-muted'
              )}
            >
              {SCHOOLS.map((school) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Combat Stats */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Combat Stats</Label>
            {!isWizard && (
              <Badge variant="secondary" className="text-[10px]">
                Wizard -2 (except Move)
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2 rounded-lg bg-muted/50 p-3">
            <StatInput
              icon={<Footprints className="h-3 w-3" />}
              label="Move"
              id={`${role}-move`}
              value={stats.move}
              onChange={(v) => handleStatChange('move', v)}
              disabled={!isWizard}
              hint={!isWizard && wizardStats ? `=${wizardStats.move}` : undefined}
            />
            <StatInput
              icon={<Swords className="h-3 w-3" />}
              label="Fight"
              id={`${role}-fight`}
              value={stats.fight}
              onChange={(v) => handleStatChange('fight', v)}
              disabled={!isWizard}
              hint={!isWizard && wizardStats ? `${wizardStats.fight}-2` : undefined}
            />
            <StatInput
              icon={<Target className="h-3 w-3" />}
              label="Shoot"
              id={`${role}-shoot`}
              value={stats.shoot}
              onChange={(v) => handleStatChange('shoot', v)}
              disabled={!isWizard}
              hint={!isWizard && wizardStats ? `${wizardStats.shoot}-2` : undefined}
            />
            <StatInput
              icon={<Shield className="h-3 w-3" />}
              label="Armor"
              id={`${role}-armor`}
              value={stats.armor}
              onChange={(v) => handleStatChange('armor', v)}
              disabled={!isWizard}
              hint={!isWizard && wizardStats ? `${wizardStats.armor}-2` : undefined}
            />
            <StatInput
              icon={<Brain className="h-3 w-3" />}
              label="Will"
              id={`${role}-will`}
              value={stats.will}
              onChange={(v) => handleStatChange('will', v)}
              disabled={!isWizard}
              hint={!isWizard && wizardStats ? `${wizardStats.will}-2` : undefined}
            />
          </div>
        </div>

        {/* Level, XP, Health Row */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`${role}-level`} className="flex items-center gap-1">
              <Star className="h-3 w-3" />
              Level
            </Label>
            <Input
              id={`${role}-level`}
              type="number"
              min={0}
              value={caster.level}
              onChange={(e) => handleLevelChange(parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${role}-xp`}>XP</Label>
            <Input
              id={`${role}-xp`}
              type="number"
              min={0}
              value={caster.xp}
              onChange={(e) => handleXpChange(parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${role}-health`} className="flex items-center gap-1">
              <Heart className="h-3 w-3 text-red-500" />
              Health
            </Label>
            <div className="flex items-center gap-1">
              <Input
                id={`${role}-health`}
                type="number"
                min={0}
                max={caster.health.max}
                value={caster.health.current}
                onChange={(e) => handleHealthChange(parseInt(e.target.value) || 0)}
                className="w-14"
              />
              <span className="text-muted-foreground">/</span>
              <Input
                type="number"
                min={1}
                value={caster.health.max}
                onChange={(e) => handleMaxHealthChange(parseInt(e.target.value) || 1)}
                className="w-14"
                aria-label="Max health"
              />
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              Items
              <Badge variant="outline">
                {caster.items.length}/{itemCap}
              </Badge>
            </Label>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              Wizard Sheet
            </span>
          </div>

          {/* Item List */}
          <div className="space-y-2">
            {caster.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-md border border-border bg-muted/50 px-3 py-2"
              >
                <span className="text-sm">{item.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveItem(item.id)}
                  aria-label={`Remove ${item.name}`}
                  className="h-8 w-8"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {caster.items.length === 0 && (
              <p className="py-2 text-center text-sm text-muted-foreground">No items yet</p>
            )}
          </div>

          {/* Add Item */}
          <Sheet open={isAddingItem} onOpenChange={setIsAddingItem}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!canAddItem}
                className="w-full"
                onClick={() => setIsAddingItem(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Item
                {!canAddItem && ' (Cap Reached)'}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom">
              <SheetHeader>
                <SheetTitle>Add Item</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="new-item-name">Item Name</Label>
                  <Input
                    id="new-item-name"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder="Enter item name"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddItem();
                    }}
                  />
                </div>
                <Button onClick={handleAddItem} disabled={!newItemName.trim()} className="w-full">
                  Add Item
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor={`${role}-notes`}>Notes</Label>
          <textarea
            id={`${role}-notes`}
            value={caster.notes || ''}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Add notes..."
            rows={2}
            className={cn(
              'flex w-full rounded-md border border-input bg-background px-3 py-2',
              'text-sm placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-ring focus-visible:ring-offset-2'
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}
